// src/pages/Login.js
import { useState } from "react";
import { Link } from "react-router-dom";
import { BsEyeSlash, BsEye } from "react-icons/bs";
import { toast } from "react-toastify";
import Spinner from "../../components/Spinner";
import sideImg from "../../assets/pic/login_signup_img_.png";
import api from "../../utils/api"; // Import the API utility
import log from "../../utils/logger"; // Import the logger

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    log.info('Login attempt started');

    try {
      const response = await api.post("/auth/login", { email, password, role: "admin" });
      const data = response.data;

      setIsLoading(false);

      if (data.code === 400) {
        handleErrorResponse(data.message);
      } else if (data.code === 200) {
        handleSuccessResponse(data.data);
      } else {
        toast.error("An unexpected error occurred. Please try again.");
        log.error(`Unexpected error: ${data.message}`);
      }
    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.data && error.response.data.message) {
        handleErrorResponse(error.response.data.message);
      } else {
        toast.error("An error occurred. Please try again.");
        log.error(`Login error: ${error.message}`);
      }
    }
  };

  const handleErrorResponse = (message) => {
    if (message === "Password is incorrect") {
      setErrorMessage("Password is incorrect.");
    } else if (message.includes("password length")) {
      setErrorMessage("Password length must be at least 5 characters long.");
    } else if (message === "No User is Found.") {
      setErrorMessage("No user found with this email.");
    } else if (message === "Invalid role") {
      setErrorMessage("Invalid role.");
    } else {
      setErrorMessage("Your credentials are wrong.");
    }
    log.warn(`Login failed: ${message}`);
  };

  const handleSuccessResponse = (data) => {
    localStorage.setItem("ID", data._id);
    localStorage.setItem("token", data.token);
    toast.success("Logged in successfully");
    log.info('Login successful');
    window.location.href = "/dashboard/home";
  };

  return (
    <section className="flex xl:flex-row flex-col h-screen">
      <div className="hidden xl:flex items-center justify-center w-full xl:w-1/2 bg-gray-50">
        <img src={sideImg} alt="Login and Signup" className="h-auto" />
      </div>
      <div className="flex flex-col items-center justify-center mt-20 w-full xl:w-1/2 p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">TobreCards Admin Dashboard</h1>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {errorMessage && (
              <p className="text-sm text-primary text-center">{errorMessage}</p>
            )}
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">Email address</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  className={`appearance-none rounded-none relative block w-full px-3 py-2 border-b-2 ${
                    errorMessage ? "border-primary" : "border-gray-300"
                  } placeholder-gray-500 focus:outline-none sm:text-sm`}
                  placeholder="Email address"
                />
              </div>
              <div className="relative">
                <label htmlFor="password" className="sr-only">Password</label>
                <input
                  type={isPasswordVisible ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className={`appearance-none rounded-none relative block w-full px-3 py-2 border-b-2 ${
                    errorMessage ? "border-primary" : "border-gray-300"
                  } placeholder-gray-500 focus:outline-none mt-2 focus:z-10 sm:text-sm`}
                  placeholder="Password"
                />
                <div
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 cursor-pointer"
                  onClick={togglePasswordVisibility}
                >
                  {isPasswordVisible ? (
                    <BsEye className="text-gray-400" />
                  ) : (
                    <BsEyeSlash className="text-gray-400" />
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/forgot_password" className="font-medium text-blue-600 hover:text-blue-500">
                  Forgot your password?
                </Link>
              </div>
            </div>
            <div>
              {isLoading ? (
                <Spinner size="80" />
              ) : (
                <button
                  type="submit"
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Sign In
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Login;
